import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { useRootStore } from './RootStore';

type Notification = {
  id: string;
  message: string;
  type: 'success' | 'error' | 'warning' | 'info';
  timestamp: number;
};

interface UIStoreState {
  sidebarCollapsed: boolean;
  activeTheme: 'light' | 'dark';
  notifications: Notification[];
  toggleSidebar: () => void;
  toggleTheme: () => void;
  addNotification: (message: string, type?: 'success' | 'error' | 'warning' | 'info') => string;
  removeNotification: (id: string) => void;
  clearAllNotifications: () => void;
}

const useUIStore = create<UIStoreState>()(
  devtools(
    persist(
      (set, get) => ({
        sidebarCollapsed: false,
        activeTheme: 'light',
        notifications: [],

        toggleSidebar: () => {
          set((state) => ({
            sidebarCollapsed: !state.sidebarCollapsed,
          }));
        },

        toggleTheme: () => {
          set((state) => ({
            activeTheme: state.activeTheme === 'light' ? 'dark' : 'light',
          }));
        },

        addNotification: (message, type = 'info') => {
          const id = Date.now().toString();
          const newNotification = {
            id,
            message,
            type,
            timestamp: Date.now(),
          };

          set((state) => ({
            notifications: [...state.notifications, newNotification],
          }));

          // Auto-remove notification after 5 seconds
          setTimeout(() => {
            get().removeNotification(id);
          }, 5000);

          return id;
        },

        removeNotification: (id) => {
          set((state) => ({
            notifications: state.notifications.filter((n) => n.id !== id),
          }));
        },

        clearAllNotifications: () => {
          set({ notifications: [] });
        },
      }),
      {
        name: 'ui-storage',
        partialize: (state) => ({
          activeTheme: state.activeTheme,
          sidebarCollapsed: state.sidebarCollapsed,
        }),
      }
    ),
    {
      name: 'ui-store',
    }
  )
);

// Register the store with the root store
const initializeStore = () => {
  // Register this store with the root store
  useRootStore.getState().setUIStore(useUIStore);
};

// Initialize the store when this module is imported
initializeStore();

export default useUIStore;
export type { UIStoreState };

